import { Link } from "react-router-dom";
import { useLang } from "../../contexts/LanguageContext";
import { ArrowUpRight } from "lucide-react";

/**
 * Editorial 3-card grid used on the Home page for either news or activities.
 * Props:
 *   items         — array of records with slug, title_xx, excerpt_xx or description_xx, cover_image, tag/category
 *   basePath      — link prefix for each card, e.g. "/noticias-socios" or "/actividades" (default: news)
 *   seeAllPath    — "Ver todo" target (default: same as basePath)
 *   kickerKey     — i18n key for the small red kicker (default: "home.newsKicker")
 *   titleKey      — i18n key for the section title (default: "home.newsTitle")
 *   excerptFields — ordered list of prefixes to try for the summary line (default: ["excerpt", "description"])
 */
export default function NewsGrid({
  items,
  basePath = "/noticias-socios",
  seeAllPath,
  kickerKey = "home.newsKicker",
  titleKey = "home.newsTitle",
  excerptFields = ["excerpt", "description"],
}) {
  const { t, pick } = useLang();
  const target = seeAllPath || basePath;

  if (!items || items.length === 0) return null;

  // Strip HTML tags for a clean text preview and truncate to a reasonable length.
  const summaryOf = (n) => {
    for (const f of excerptFields) {
      const raw = pick(n, f);
      if (raw && String(raw).trim()) {
        const text = String(raw).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
        return text.length > 180 ? text.slice(0, 177) + "…" : text;
      }
    }
    return "";
  };

  return (
    <section className="bg-[#FAFAFA] py-24" data-testid="news-grid-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#C8102E] mb-3">
              {t(kickerKey)}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0F2040]">{t(titleKey)}</h2>
          </div>
          <Link
            to={target}
            className="text-sm font-medium text-[#0F2040] hover:text-[#C8102E] inline-flex items-center gap-2 transition-colors"
            data-testid="news-see-all"
          >
            {t("home.seeAll")} <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {items.slice(0, 3).map((n) => (
            <article
              key={n.id}
              className="bg-white border border-gray-100 group hover:shadow-xl transition-shadow"
              data-testid={`news-card-${n.slug}`}
            >
              {n.cover_image && (
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={n.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
              )}
              <div className="p-7">
                {(n.tag || n.category) && (
                  <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#C8102E]">
                    {n.tag || n.category}
                  </span>
                )}
                <h3 className="font-serif text-2xl text-[#0F2040] mt-3 mb-3 leading-tight">{pick(n, "title")}</h3>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed line-clamp-3">{summaryOf(n)}</p>
                <Link
                  to={`${basePath}/${n.slug}`}
                  className="text-sm font-medium text-[#C8102E] hover:text-[#0F2040] inline-flex items-center gap-1.5 transition-colors"
                >
                  {t("common.readMore")} →
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
